/**
 * Procedurális textúra-generálás.
 *
 * Nincs hálózati asset. Minden felület betöltéskor, `<canvas>`-on készül,
 * magolt zajból. A generátorok nem közvetlenül színt írnak, hanem két
 * mezőt töltenek ki:
 *   - `h` magasságtérkép (0..1) → ebből jön az árnyékolás (AO) és a normálmap
 *   - `s` rámpa-pozíció (0..1)  → ebből jön a szín a családi rámpából
 * A közös lezárás (`finish`) adja hozzá a piszkot, az AO-t és a
 * paletta-kvantálást — ez köti vizuálisan egybe az összes felületet.
 *
 * Gazda: C
 */

import * as THREE from 'three'
import { Rng } from '../core/rng'
import { RAMPS, type RampName, sampleRamp, quantizeToPalette } from './palette'

const SIZE = 128

// ---------------------------------------------------------------------------
// Generátor-kontextus
// ---------------------------------------------------------------------------

class TexGen {
  readonly size = SIZE
  /** magasság 0..1 */
  readonly h = new Float32Array(SIZE * SIZE)
  /** rámpa-pozíció 0..1 */
  readonly s = new Float32Array(SIZE * SIZE)
  /** önfény 0..1 */
  readonly e = new Float32Array(SIZE * SIZE)
  /**
   * Rámpán kívüli színréteg keverési aránya (0..1). Olyan foltokhoz kell,
   * amelyek NEM a felület anyagából jönnek: vér, korom, alga.
   */
  readonly t = new Float32Array(SIZE * SIZE)
  readonly rng: Rng

  constructor(seed: number) {
    this.rng = new Rng(seed)
  }

  idx(x: number, y: number): number {
    return ((y & (SIZE - 1)) * SIZE + (x & (SIZE - 1))) | 0
  }

  fill(shade: number, height: number): void {
    this.s.fill(shade)
    this.h.fill(height)
  }

  /** Tömör téglalap (tekeredéssel). */
  rect(x0: number, y0: number, w: number, hgt: number, shade: number, height: number, emis = 0): void {
    for (let y = y0; y < y0 + hgt; y++) {
      for (let x = x0; x < x0 + w; x++) {
        const i = this.idx(x, y)
        this.s[i] = shade
        this.h[i] = height
        if (emis > 0) this.e[i] = emis
      }
    }
  }

  /**
   * Csempézhető value-noise. `period` a rácsfelbontás — a textúra szélének
   * osztója kell legyen, különben látszik a varrat.
   */
  noise(period: number, seed: number): Float32Array {
    const g = new Float32Array(period * period)
    const r = this.rng.fork(seed)
    for (let i = 0; i < g.length; i++) g[i] = r.next()

    const out = new Float32Array(SIZE * SIZE)
    const scale = period / SIZE
    for (let y = 0; y < SIZE; y++) {
      const fy = y * scale
      const y0 = Math.floor(fy) % period
      const y1 = (y0 + 1) % period
      const ty = fy - Math.floor(fy)
      const wy = ty * ty * (3 - 2 * ty)
      for (let x = 0; x < SIZE; x++) {
        const fx = x * scale
        const x0 = Math.floor(fx) % period
        const x1 = (x0 + 1) % period
        const tx = fx - Math.floor(fx)
        const wx = tx * tx * (3 - 2 * tx)
        const a = g[y0 * period + x0] + (g[y0 * period + x1] - g[y0 * period + x0]) * wx
        const b = g[y1 * period + x0] + (g[y1 * period + x1] - g[y1 * period + x0]) * wx
        out[y * SIZE + x] = a + (b - a) * wy
      }
    }
    return out
  }

  /** Több oktávos csempézhető zaj. */
  fbm(basePeriod: number, octaves: number, seed: number): Float32Array {
    const out = new Float32Array(SIZE * SIZE)
    let amp = 1
    let total = 0
    for (let o = 0; o < octaves; o++) {
      const p = basePeriod * Math.pow(2, o)
      if (p > SIZE) break
      const n = this.noise(p, seed + o * 977)
      for (let i = 0; i < out.length; i++) out[i] += n[i] * amp
      total += amp
      amp *= 0.5
    }
    for (let i = 0; i < out.length; i++) out[i] /= total || 1
    return out
  }

  /** Csavar/szegecs — kis kiemelkedés a megadott középpontban. */
  bolt(cx: number, cy: number, r: number, shade: number): void {
    for (let y = -r - 1; y <= r + 1; y++) {
      for (let x = -r - 1; x <= r + 1; x++) {
        const d = Math.sqrt(x * x + y * y)
        if (d > r + 0.5) continue
        const i = this.idx(cx + x, cy + y)
        const dome = Math.sqrt(Math.max(0, 1 - (d / r) * (d / r)))
        this.h[i] = Math.min(1, 0.55 + dome * 0.45)
        // A csavar teteje világosabb, az alja sötétebb — kis térbeli utalás.
        this.s[i] = shade + dome * 0.12 - (y > 0 ? 0.06 : 0)
      }
    }
  }

  /** Vízszintes horony (fuga, panelvágás). */
  groove(y0: number, thickness: number, shade: number, depth: number): void {
    this.rect(0, y0, SIZE, thickness, shade, depth)
  }

  /** Véletlen bolyongó repedés a felületen. */
  crack(seed: number, length: number, depth: number, shade: number): void {
    const r = this.rng.fork(seed)
    let x = r.next() * SIZE
    let y = r.next() * SIZE
    let a = r.next() * Math.PI * 2
    for (let i = 0; i < length; i++) {
      a += r.spread(0.45)
      x += Math.cos(a)
      y += Math.sin(a)
      const idx = this.idx(Math.round(x), Math.round(y))
      this.h[idx] = Math.min(this.h[idx], depth)
      this.s[idx] = Math.min(this.s[idx], shade)
      // Elvékonyodó szélek, hogy ne egy pixel vastag vonal legyen.
      if (r.chance(0.4)) {
        const j = this.idx(Math.round(x) + (r.chance(0.5) ? 1 : -1), Math.round(y))
        this.h[j] = Math.min(this.h[j], depth + 0.15)
        this.s[j] = Math.min(this.s[j], shade + 0.05)
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Lezárás: AO + piszok + kvantálás → ImageData
// ---------------------------------------------------------------------------

interface FinishOpts {
  ramp: RampName
  /** Mennyire piszkolja be a fbm zaj a rámpa-pozíciót. */
  grime?: number
  /** Az AO ereje a magasságkülönbség alapján. */
  ao?: number
  /** Dithering erőssége (0 = kikapcsolva). */
  dither?: number
  /** Emissziós szín, ha van önfényes rész. */
  emissiveColor?: number
  /** A `t` réteg színe (vér, korom); a rámpa fölé keveredik. */
  tintColor?: number
}

function finish(g: TexGen, opts: FinishOpts): { albedo: ImageData; height: Float32Array; emissive: ImageData | null } {
  const ramp = RAMPS[opts.ramp]
  const grime = opts.grime ?? 0.18
  const aoStrength = opts.ao ?? 0.9

  const dirt = g.fbm(8, 4, 4242)
  const fine = g.noise(64, 991)

  const img = new ImageData(SIZE, SIZE)
  const d = img.data

  let hasEmissive = false
  for (let i = 0; i < g.e.length; i++) {
    if (g.e[i] > 0) {
      hasEmissive = true
      break
    }
  }
  const emis = hasEmissive ? new ImageData(SIZE, SIZE) : null

  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      const i = y * SIZE + x

      // Ambient occlusion: a szomszédoknál mélyebb pontok sötétebbek.
      const hc = g.h[i]
      const hl = g.h[g.idx(x - 1, y)]
      const hr = g.h[g.idx(x + 1, y)]
      const hu = g.h[g.idx(x, y - 1)]
      const hd = g.h[g.idx(x, y + 1)]
      const occl = Math.max(0, (hl + hr + hu + hd) * 0.25 - hc)

      // Irányított megvilágítás bal-felülről: a felfelé néző élek világosabbak.
      const bevel = (hu - hd) * 0.35 + (hl - hr) * 0.25

      let t = g.s[i]
      t -= occl * aoStrength
      t += bevel
      t += (dirt[i] - 0.5) * grime * 2
      t += (fine[i] - 0.5) * 0.05
      // Alapszint-emelés: a generátorok a rámpa alsó harmadában dolgoznak,
      // a megvilágítási modell viszont onnan feketébe húzná őket. A sávot
      // felfelé toljuk, a kontrasztot megtartva.
      t = 0.2 + t * 0.84
      t = Math.max(0, Math.min(1, t))

      const c = sampleRamp(ramp, t)
      const o = i * 4

      const tintAmt = g.t[i]
      if (tintAmt > 0) {
        const tc = opts.tintColor ?? 0x5c0a0f
        // A folt sötétíti is a felületet, nem csak színezi — a nedves
        // anyag elnyeli a fényt.
        const shadow = 1 - tintAmt * 0.35
        d[o] = c[0] * shadow * (1 - tintAmt) + ((tc >> 16) & 255) * tintAmt
        d[o + 1] = c[1] * shadow * (1 - tintAmt) + ((tc >> 8) & 255) * tintAmt
        d[o + 2] = c[2] * shadow * (1 - tintAmt) + (tc & 255) * tintAmt
      } else {
        d[o] = c[0]
        d[o + 1] = c[1]
        d[o + 2] = c[2]
      }
      d[o + 3] = 255

      if (emis) {
        const ev = g.e[i]
        const ec = opts.emissiveColor ?? 0xffffff
        emis.data[o] = ((ec >> 16) & 255) * ev
        emis.data[o + 1] = ((ec >> 8) & 255) * ev
        emis.data[o + 2] = (ec & 255) * ev
        emis.data[o + 3] = 255
      }
    }
  }

  quantizeToPalette(d, SIZE, opts.dither ?? 1)
  return { albedo: img, height: g.h, emissive: emis }
}

// ---------------------------------------------------------------------------
// Konkrét generátorok
// ---------------------------------------------------------------------------

type TexRecipe = (g: TexGen) => FinishOpts

const GENERATORS: Record<string, TexRecipe> = {
  /** Sima, kopott beton — az alapfelület. */
  concrete: (g) => {
    const n = g.fbm(4, 5, 11)
    for (let i = 0; i < n.length; i++) {
      g.s[i] = 0.34 + n[i] * 0.22
      g.h[i] = 0.55 + n[i] * 0.12
    }
    for (let c = 0; c < 5; c++) g.crack(200 + c, 90, 0.15, 0.18)
    return { ramp: 'stone', grime: 0.22, ao: 1.1 }
  },

  /** Offszetelt téglasorok mély fugával. */
  brick: (g) => {
    const n = g.fbm(8, 4, 33)
    g.fill(0.45, 0.75)
    const rowH = 16
    const brickW = 32
    const mortar = 3
    for (let row = 0; row < SIZE / rowH; row++) {
      const y0 = row * rowH
      g.groove(y0, mortar, 0.16, 0.25)
      const offset = (row & 1) === 0 ? 0 : brickW / 2
      for (let b = 0; b <= SIZE / brickW; b++) {
        const x0 = (b * brickW + offset) % SIZE
        g.rect(x0, y0, mortar, rowH, 0.16, 0.25)
      }
    }
    // Téglánkénti színszórás — enélkül gépiesen egyforma lenne a fal.
    for (let row = 0; row < SIZE / rowH; row++) {
      const offset = (row & 1) === 0 ? 0 : brickW / 2
      for (let b = 0; b <= SIZE / brickW; b++) {
        const jitter = g.rng.spread(0.07)
        const x0 = (b * brickW + offset + mortar) % SIZE
        for (let y = row * rowH + mortar; y < (row + 1) * rowH; y++) {
          for (let x = x0; x < x0 + brickW - mortar; x++) {
            const i = g.idx(x, y)
            g.s[i] = Math.max(0.08, g.s[i] + jitter + (n[i] - 0.5) * 0.18)
          }
        }
      }
    }
    for (let c = 0; c < 3; c++) g.crack(400 + c, 60, 0.3, 0.2)
    return { ramp: 'rust', grime: 0.14, ao: 1.3 }
  },

  /**
   * Ipari fémpanel. EGY panel tölti ki a teljes csempét — a korábbi 2×2-es
   * rács a világban látható „csempézettséget" okozott. Így egy panel ~3.6
   * egység széles, ami építészeti léptékként olvasódik, nem mintázatként.
   */
  techPanel: (g) => {
    const n = g.fbm(8, 4, 77)
    g.fill(0.46, 0.72)

    // Süllyesztett panelkeret a csempe szélén (a szomszéd csempével együtt
    // ez adja a panelek közti hézagot).
    const b = 5
    g.rect(0, 0, SIZE, b, 0.16, 0.26)
    g.rect(0, SIZE - b, SIZE, b, 0.16, 0.26)
    g.rect(0, 0, b, SIZE, 0.16, 0.26)
    g.rect(SIZE - b, 0, b, SIZE, 0.16, 0.26)

    // Belső, enyhén kiemelkedő mező
    g.rect(b + 3, b + 3, SIZE - 2 * b - 6, SIZE - 2 * b - 6, 0.5, 0.8)

    // Négy sarokcsavar
    for (const [cx, cy] of [
      [14, 14],
      [SIZE - 14, 14],
      [14, SIZE - 14],
      [SIZE - 14, SIZE - 14],
    ]) {
      g.bolt(cx, cy, 3.5, 0.62)
    }

    // Egyetlen világító sáv a panel közepén, süllyesztett ágyban.
    g.rect(18, 60, SIZE - 36, 9, 0.2, 0.42)
    g.rect(20, 62, SIZE - 40, 5, 0.9, 0.5, 0.95)

    // Szellőzőrács a panel alsó harmadában — megtöri az üres mezőt.
    for (let i = 0; i < 5; i++) {
      g.rect(30, 88 + i * 6, SIZE - 60, 3, 0.22, 0.4)
    }

    for (let i = 0; i < g.s.length; i++) {
      if (g.e[i] === 0) g.s[i] += (n[i] - 0.5) * 0.15
    }
    for (let c = 0; c < 3; c++) g.crack(880 + c, 30, 0.4, 0.3)
    return { ramp: 'tech', grime: 0.16, ao: 1.15, emissiveColor: 0x64c4cf }
  },

  /** Függőlegesen bordázott fém. */
  metalRibbed: (g) => {
    const n = g.fbm(16, 3, 121)
    for (let y = 0; y < SIZE; y++) {
      for (let x = 0; x < SIZE; x++) {
        const i = y * SIZE + x
        const rib = Math.abs(((x % 16) - 8) / 8)
        g.h[i] = 0.35 + (1 - rib) * 0.5
        g.s[i] = 0.28 + (1 - rib) * 0.3 + (n[i] - 0.5) * 0.16
      }
    }
    for (let c = 0; c < 4; c++) g.crack(700 + c, 45, 0.25, 0.16)
    return { ramp: 'stone', grime: 0.2, ao: 0.8 }
  },

  /** Egyetlen nagy fémlap szegecssorral, karcokkal, rozsdafoltokkal. */
  metalPlate: (g) => {
    const n = g.fbm(8, 4, 303)
    const rust = g.fbm(4, 3, 909)
    g.fill(0.48, 0.74)

    // Vízszintes lapillesztés a csempe alján és tetején.
    g.rect(0, 0, SIZE, 4, 0.2, 0.34)
    g.rect(0, SIZE - 4, SIZE, 4, 0.2, 0.34)

    // Szegecssor az illesztések mentén.
    for (let k = 0; k < 8; k++) {
      g.bolt(10 + k * 16, 10, 2.5, 0.64)
      g.bolt(10 + k * 16, SIZE - 10, 2.5, 0.64)
    }

    // Függőleges merevítő borda a lap közepén.
    g.rect(60, 6, 8, SIZE - 12, 0.6, 0.92)

    for (let i = 0; i < g.s.length; i++) {
      // Rozsdafolt: küszöbölt zaj, hogy összefüggő foltokat adjon.
      const r = Math.max(0, rust[i] - 0.55) * 2.2
      g.s[i] += (n[i] - 0.5) * 0.14 - r * 0.24
      g.h[i] -= r * 0.05
    }
    for (let c = 0; c < 6; c++) g.crack(1100 + c, 40, 0.42, 0.26)
    return { ramp: 'rust', grime: 0.17, ao: 1.05 }
  },

  /** Padlórács — sötét lyukakkal, mélységérzettel. */
  grate: (g) => {
    g.fill(0.5, 0.85)
    const cell = 16
    for (let cy = 0; cy < SIZE / cell; cy++) {
      for (let cx = 0; cx < SIZE / cell; cx++) {
        g.rect(cx * cell + 4, cy * cell + 4, cell - 7, cell - 7, 0.05, 0.02)
      }
    }
    const n = g.fbm(8, 3, 55)
    for (let i = 0; i < g.s.length; i++) g.s[i] += (n[i] - 0.5) * 0.1
    return { ramp: 'stone', grime: 0.1, ao: 1.6 }
  },

  /** Átlós figyelmeztető csíkozás — csak veszélyes peremekre. */
  hazardStripe: (g) => {
    const n = g.fbm(8, 3, 66)
    for (let y = 0; y < SIZE; y++) {
      for (let x = 0; x < SIZE; x++) {
        const i = y * SIZE + x
        const band = (((x + y) % 32) < 16)
        g.s[i] = band ? 0.82 : 0.14
        g.h[i] = 0.6
      }
    }
    // Erős kopás: az új, tiszta csíkozás műanyagnak nézne ki.
    for (let i = 0; i < g.s.length; i++) {
      const wear = Math.max(0, n[i] - 0.45) * 1.8
      g.s[i] -= wear * 0.5
      g.h[i] -= wear * 0.2
    }
    for (let c = 0; c < 8; c++) g.crack(1500 + c, 35, 0.35, 0.1)
    return { ramp: 'hazard', grime: 0.1, ao: 0.7 }
  },

  /** Durva barlangkő. */
  rock: (g) => {
    const big = g.fbm(4, 3, 17)
    const small = g.fbm(16, 3, 18)
    for (let i = 0; i < g.s.length; i++) {
      const v = big[i] * 0.7 + small[i] * 0.3
      g.h[i] = v
      g.s[i] = 0.2 + v * 0.4
    }
    for (let c = 0; c < 8; c++) g.crack(1900 + c, 110, 0.1, 0.12)
    return { ramp: 'stone', grime: 0.28, ao: 1.4 }
  },

  /** Szerves, húsos falfelület — a démoni terekhez. */
  flesh: (g) => {
    const base = g.fbm(4, 4, 21)
    const vein = g.fbm(8, 3, 22)
    for (let i = 0; i < g.s.length; i++) {
      const v = base[i]
      const veins = Math.abs(vein[i] - 0.5) < 0.06 ? 1 : 0
      g.h[i] = 0.4 + v * 0.5 - veins * 0.3
      g.s[i] = 0.3 + v * 0.35 - veins * 0.2
    }
    return { ramp: 'flesh', grime: 0.24, ao: 1.5 }
  },

  /** Mérgező hűtőfolyadék — önfényes, a padlón. */
  nukage: (g) => {
    const a = g.fbm(4, 4, 31)
    const b = g.fbm(8, 3, 32)
    for (let i = 0; i < g.s.length; i++) {
      const v = a[i] * 0.65 + b[i] * 0.35
      g.h[i] = 0.5 + v * 0.2
      g.s[i] = 0.45 + v * 0.4
      g.e[i] = 0.25 + v * 0.4
    }
    return { ramp: 'hazard', grime: 0.06, ao: 0.4, emissiveColor: 0x8ad63a, dither: 0.6 }
  },

  /** Beltéri mennyezet: vékony gerendarács. */
  ceilingTech: (g) => {
    const n = g.fbm(8, 3, 44)
    g.fill(0.3, 0.7)
    for (let k = 0; k <= 4; k++) {
      g.groove(k * 32 - 2, 5, 0.5, 0.9)
      g.rect(k * 32 - 2, 0, 5, SIZE, 0.5, 0.9)
    }
    for (let i = 0; i < g.s.length; i++) g.s[i] += (n[i] - 0.5) * 0.14
    return { ramp: 'stone', grime: 0.18, ao: 1.2 }
  },

  /** Ajtólap — nehéz, ipari, középső osztással. */
  doorTech: (g) => {
    const n = g.fbm(8, 3, 88)
    g.fill(0.5, 0.75)
    // Középső osztóvonal
    g.rect(62, 0, 4, SIZE, 0.12, 0.25)
    // Keret
    g.rect(0, 0, 6, SIZE, 0.3, 0.9)
    g.rect(SIZE - 6, 0, 6, SIZE, 0.3, 0.9)
    g.rect(0, 0, SIZE, 6, 0.3, 0.9)
    g.rect(0, SIZE - 6, SIZE, 6, 0.3, 0.9)
    for (let k = 0; k < 4; k++) {
      g.bolt(14, 20 + k * 30, 3, 0.65)
      g.bolt(SIZE - 14, 20 + k * 30, 3, 0.65)
    }
    // Figyelmeztető sáv az ajtó közepén
    g.rect(20, 56, 88, 16, 0.85, 0.8, 0.55)
    for (let i = 0; i < g.s.length; i++) {
      if (g.e[i] === 0) g.s[i] += (n[i] - 0.5) * 0.12
    }
    return { ramp: 'rust', grime: 0.14, ao: 1.1, emissiveColor: 0xe4b02c }
  },

  /** Világító panel — fényforrás-felület. */
  lightPanel: (g) => {
    g.fill(0.95, 0.6)
    for (let i = 0; i < g.e.length; i++) g.e[i] = 0.95
    g.rect(0, 0, SIZE, 8, 0.3, 0.9, 0.1)
    g.rect(0, SIZE - 8, SIZE, 8, 0.3, 0.9, 0.1)
    g.rect(0, 0, 8, SIZE, 0.3, 0.9, 0.1)
    g.rect(SIZE - 8, 0, 8, SIZE, 0.3, 0.9, 0.1)
    return { ramp: 'hazard', grime: 0.02, ao: 0.5, emissiveColor: 0xffd98a, dither: 0.3 }
  },

  /** Padlócsempe nagy lapokból. */
  floorTile: (g) => {
    const n = g.fbm(8, 4, 99)
    g.fill(0.4, 0.75)
    for (let k = 0; k <= 2; k++) {
      g.groove(k * 64 - 2, 4, 0.14, 0.3)
      g.rect(k * 64 - 2, 0, 4, SIZE, 0.14, 0.3)
    }
    for (let i = 0; i < g.s.length; i++) g.s[i] += (n[i] - 0.5) * 0.2
    for (let c = 0; c < 4; c++) g.crack(2300 + c, 70, 0.2, 0.2)
    return { ramp: 'stone', grime: 0.24, ao: 1.1 }
  },

  /** Vérfoltos beton — a harci terekhez. */
  bloodStone: (g) => {
    const n = g.fbm(4, 5, 13)
    const blood = g.fbm(4, 4, 555)
    for (let i = 0; i < n.length; i++) {
      g.s[i] = 0.34 + n[i] * 0.22
      g.h[i] = 0.55 + n[i] * 0.12
    }
    for (let c = 0; c < 4; c++) g.crack(2700 + c, 80, 0.15, 0.18)

    // A vér nem a kő anyagából jön, ezért nem a rámpán, hanem a tint-rétegen
    // keresztül kerül rá. Küszöbölt fbm → összefüggő foltok, nem egyenletes köd.
    for (let i = 0; i < blood.length; i++) {
      const v = blood[i]
      if (v > 0.58) {
        const edge = Math.min(1, (v - 0.58) / 0.18)
        g.t[i] = edge * 0.85
        // A száradt vér kissé beszívódik: a felület enyhén mélyebb lesz.
        g.h[i] -= edge * 0.05
      }
    }
    return { ramp: 'stone', grime: 0.22, ao: 1.1, tintColor: 0x3d0509 }
  },
}

// ---------------------------------------------------------------------------
// Normálmap származtatása magasságtérképből (Sobel)
// ---------------------------------------------------------------------------

function heightToNormal(h: Float32Array, strength: number): ImageData {
  const img = new ImageData(SIZE, SIZE)
  const at = (x: number, y: number) => h[(y & (SIZE - 1)) * SIZE + (x & (SIZE - 1))]
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      const dx =
        at(x - 1, y - 1) + 2 * at(x - 1, y) + at(x - 1, y + 1) -
        (at(x + 1, y - 1) + 2 * at(x + 1, y) + at(x + 1, y + 1))
      const dy =
        at(x - 1, y - 1) + 2 * at(x, y - 1) + at(x + 1, y - 1) -
        (at(x - 1, y + 1) + 2 * at(x, y + 1) + at(x + 1, y + 1))

      let nx = dx * strength
      let ny = dy * strength
      const nz = 1
      const len = Math.sqrt(nx * nx + ny * ny + nz * nz)
      nx /= len
      ny /= len

      const o = (y * SIZE + x) * 4
      img.data[o] = (nx * 0.5 + 0.5) * 255
      img.data[o + 1] = (ny * 0.5 + 0.5) * 255
      img.data[o + 2] = (nz / len) * 0.5 * 255 + 127.5
      img.data[o + 3] = 255
    }
  }
  return img
}

// ---------------------------------------------------------------------------
// Nyilvános felület
// ---------------------------------------------------------------------------

export interface GeneratedTexture {
  map: THREE.CanvasTexture
  normalMap: THREE.CanvasTexture
  emissiveMap: THREE.CanvasTexture | null
}

function imageToTexture(img: ImageData, srgb: boolean): THREE.CanvasTexture {
  const canvas = document.createElement('canvas')
  canvas.width = SIZE
  canvas.height = SIZE
  const cx = canvas.getContext('2d')
  if (!cx) throw new Error('2D kontextus nem elérhető')
  cx.putImageData(img, 0, 0)
  const tex = new THREE.CanvasTexture(canvas)
  tex.wrapS = THREE.RepeatWrapping
  tex.wrapT = THREE.RepeatWrapping
  // Nearest nagyításnál: ez adja a szándékos, éles retro pixelt.
  tex.magFilter = THREE.NearestFilter
  tex.minFilter = THREE.LinearMipmapLinearFilter
  tex.generateMipmaps = true
  tex.anisotropy = 4
  if (srgb) tex.colorSpace = THREE.SRGBColorSpace
  return tex
}

const cache = new Map<string, GeneratedTexture>()

export function getTexture(id: string): GeneratedTexture {
  const hit = cache.get(id)
  if (hit) return hit

  const gen = GENERATORS[id]
  if (!gen) throw new Error(`Ismeretlen textúra: ${id}`)

  // A magot az azonosítóból származtatjuk: ugyanaz a textúra minden
  // futásban ugyanúgy néz ki.
  let seed = 0
  for (let i = 0; i < id.length; i++) seed = (Math.imul(seed, 31) + id.charCodeAt(i)) | 0

  const g = new TexGen(seed)
  const opts = gen(g)
  const res = finish(g, opts)

  const out: GeneratedTexture = {
    map: imageToTexture(res.albedo, true),
    normalMap: imageToTexture(heightToNormal(res.height, 2.2), false),
    emissiveMap: res.emissive ? imageToTexture(res.emissive, true) : null,
  }
  cache.set(id, out)
  return out
}

export const TEXTURE_IDS = Object.keys(GENERATORS)

/** Minden textúrát előre legenerál (betöltőképernyőhöz). */
export function* generateAll(): Generator<string, void, void> {
  for (const id of TEXTURE_IDS) {
    getTexture(id)
    yield id
  }
}
